/**
 * Motor de tradução baseado em i18next.
 *
 * Inicializa o i18next com os recursos de validação (JSON).
 * Usa a instância standalone para poder ser chamado fora de componentes React
 * (ex: schemas Zod, services, utils).
 *
 * Para componentes React, usar `useTranslation()` de `react-i18next`.
 */

import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import ptValidation from "./locales/pt-PT/validation.json";
import ptCommon from "./locales/pt-PT/common.json";
import { DEFAULT_TENANT } from "@/shared/config/tenant";

// Inicialização síncrona — recursos embutidos no bundle
if (!i18next.isInitialized) {
  i18next
    .use(initReactI18next)
    .init({
      lng: DEFAULT_TENANT.locale,
      fallbackLng: "pt-PT",
      defaultNS: "common",
      fallbackNS: "validation",
      ns: ["common", "validation"],
      // pt-PT embutido no bundle (padrão e fallback);
      // en é carregado sob demanda em setLocale().
      resources: {
        "pt-PT": {
          validation: ptValidation,
          common: ptCommon,
        },
      },
      interpolation: {
        escapeValue: false, // React já faz escape
      },
      react: {
        useSuspense: false
      }
    });
  if (DEFAULT_TENANT.locale !== "pt-PT") setLocale(DEFAULT_TENANT.locale);
}

/**
 * Retorna a mensagem traduzida para a chave informada.
 * Usa a separação por ponto como nesting ("project.title.required").
 * Se a chave não for encontrada, retorna a chave como fallback.
 */
export function t(key: string, options?: Record<string, unknown>): string {
  return i18next.t(key, { defaultValue: key, ...options }) as string;
}

// Só o pt-PT vai no bundle; os demais idiomas são baixados na primeira troca.
async function ensureLocaleLoaded(locale: string): Promise<void> {
  if (locale !== "en" || i18next.hasResourceBundle("en", "common")) return;
  const [common, validation] = await Promise.all([
    import("./locales/en/common.json"),
    import("./locales/en/validation.json"),
  ]);
  i18next.addResourceBundle("en", "common", common.default, true, true);
  i18next.addResourceBundle("en", "validation", validation.default, true, true);
}

/** Altera o locale activo em runtime. */
export function setLocale(locale: string): void {
  void ensureLocaleLoaded(locale).then(() => i18next.changeLanguage(locale));
}

/** Retorna o locale activo. */
export function getLocale(): string {
  return i18next.language;
}

/** A instância i18next para uso avançado. */
;

/** Tipo utilitário para chaves conhecidas. */
type TranslationKey = string;
